import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Link from "@mui/material/Link";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Close from "@mui/icons-material/Close";
import ShoppingCartOutlined from "@mui/icons-material/ShoppingCartOutlined";
import { useAppDispatch } from "../../redux/hooks";
import {
  getAllCartwithItemsByUser,
  removeItemOfCart,
} from "../../redux/cart/cartItem";

interface CartProps {
  fullPage?: boolean;
  // Optionnel : permet de surcharger le comportement de suppression par défaut
  // (ex. afficher une confirmation avant de supprimer).
  onRemoveItem?: (item: any) => void;
}

const COLORS = {
  title: "#1B374C",
  muted: "#526477",
  light: "#718096",
  border: "#E5E9ED",
  header: "#F5F7F9",
  orange: "#F39325",
  orangeDark: "#DF7B12",
  orangeText: "#B85A0A",
  orangeBg: "#FFF3E8",
  orangeBorder: "#FFD0A6",
  chipBg: "#FFF0E2",
};

const Cart: React.FC<CartProps> = ({ fullPage = false, onRemoveItem }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const cartsState = useSelector((state: any) => state.cart?.Carts);
  const loading = useSelector((state: any) => state.cart?.loading);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const language = i18n.language || "fr";
  React.useEffect(() => {
    if (fullPage) {
      dispatch(getAllCartwithItemsByUser());
    }
  }, [dispatch, fullPage]);

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  const getTranslatedValue = (value: any): string => {
    if (typeof value === "string" || typeof value === "number") {
      return String(value);
    }
    if (value && typeof value === "object") {
      const locale = language.toUpperCase();
      return String(value[locale] || value.FR || value.EN || value.NL || "");
    }
    return "";
  };

  const formatPrice = (value: number) =>
    new Intl.NumberFormat(language, {
      style: "currency",
      currency: "EUR",
    }).format(value);

  const getPrice = (item: any): number | null => {
    const value = Number(
      item.amount ??
        item.price ??
        item.tarif ??
        item.totalAmount ??
        item.Subscription?.tarif ??
        item.subscription?.tarif,
    );
    return Number.isFinite(value) ? value : null;
  };

  const getSubscriptionName = (item: any) =>
    getTranslatedValue(item?.Subscription?.name ?? item?.subscription?.name);

  const getParking = (item: any) =>
    item?.parking ||
    item?.Parking ||
    item?.__cart?.parking ||
    item?.__cart?.Parking ||
    {};

  const getParkingName = (item: any) =>
    getTranslatedValue(
      getParking(item).parkingName ||
        item?.parkingName ||
        item?.__cart?.parkingName,
    );

  const getParkingAddress = (item: any) => {
    const parking = getParking(item);
    return getTranslatedValue(
      parking.adress || parking.address || item?.parkingAddress,
    );
  };

  const getVehicle = (item: any): string =>
    item?.vehicle?.licensePlate ||
    item?.vehicle?.name ||
    item?.licensePlate ||
    item?.vehicleNumber ||
    item?.vehicleNb?.licensePlate ||
    item?.vcs ||
    "";

  const getDriver = (item: any): string =>
    item?.driver?.fullName || item?.Driver?.fullName || item?.driverName || "";

  // -------------------------------------------------------------------------
  // Données : paniers -> items (en attente de paiement)
  // -------------------------------------------------------------------------

  const carts = Array.isArray(cartsState)
    ? cartsState
    : Array.isArray(cartsState?.Carts)
    ? cartsState.Carts
    : [];

  const cartItems = carts
    .flatMap((cart: any) => {
      const nested =
        cart?.CartItems ?? cart?.cartItems ?? cart?.Items ?? cart?.items;
      const items = Array.isArray(nested)
        ? nested
        : nested && typeof nested === "object"
        ? Object.values(nested)
        : [];
      if (items.length)
        return items.map((item: any) => ({ ...item, __cart: cart }));
      return cart?.Subscription || cart?.subscription || cart?.name
        ? [{ ...cart, __cart: cart }]
        : [];
    })
    .filter((item: any) => {
      const paymentStatus = String(
        item?.paymentStatus ?? item?.PaymentStatus ?? "",
      ).toLowerCase();
      const status = String(item?.status ?? item?.Status ?? "").toLowerCase();
      return paymentStatus === "pending" || status === "pending";
    });

  const itemCount = cartItems.reduce((total: number, item: any) => {
    const quantity = Number(item?.quantity ?? item?.Quantity ?? 1);
    return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
  }, 0);

  // Entité juridique (EJName) -> groupes par date de début (payés ensemble)
  const entities = cartItems.reduce((result: any[], item: any) => {
    const cart = item.__cart || {};
    const entityName = getTranslatedValue(cart.EJName || item.EJName);
    const entityKey = cart.EJID || item.EJID || entityName || "default";

    let entity = result.find((e) => e.key === entityKey);
    if (!entity) {
      entity = { key: entityKey, name: entityName, groups: [] };
      result.push(entity);
    }

    const rawDate = item.startTime || cart.startTime;
    const parsed = rawDate ? new Date(rawDate) : null;
    const date = parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
    const dateKey = date
      ? `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
      : "undated";

    let group = entity.groups.find((g: any) => g.key === dateKey);
    if (!group) {
      group = { key: dateKey, date, items: [] };
      entity.groups.push(group);
    }
    group.items.push(item);
    return result;
  }, []);

  const parkingsCount = new Set(
    cartItems.map(
      (item: any) =>
        item.parkingId || getParking(item).id || getParkingName(item),
    ),
  ).size;

  // -------------------------------------------------------------------------
  // Navigation & actions
  // -------------------------------------------------------------------------

  const goToSubscriptions = () => {
    setAnchorEl(null);
    navigate("/profile", { state: { value: 2 } });
  };
  const goToPaiement = (group: any) => {
    setAnchorEl(null);
    navigate("/panier/paiement", {
      state: {
        ids: group.items.map((item: any) => item.id),
        isSepa: false,
      },
    });
  };
  const goToCartPage = () => {
    setAnchorEl(null);
    navigate("/panier");
  };
  const openCart = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    dispatch(getAllCartwithItemsByUser());
  };

  // Suppression d'un abonnement du panier
  const handleRemoveItem = async (item: any) => {
    if (onRemoveItem) {
      onRemoveItem(item);
      return;
    }
    // Corps envoyé à DELETE /delete-item-cart : adapte les clés à ton backend
    const result: any = await dispatch(
      removeItemOfCart({ cartItemId: item.id, cartId: item.Cart?.id }),
    );

    if (result?.payload?.success) {
      dispatch(getAllCartwithItemsByUser());
    }
  };

  // -------------------------------------------------------------------------
  // Contenu (partagé entre le popover et la page complète)
  // -------------------------------------------------------------------------

  const renderBanner = () =>
    parkingsCount > 1 ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          mb: 2,
          p: 1.5,
          color: COLORS.orangeText,
          bgcolor: COLORS.orangeBg,
          border: `1px solid ${COLORS.orangeBorder}`,
          borderRadius: "8px",
        }}
      >
        <InfoOutlined sx={{ fontSize: 16, mt: "1px", flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12, lineHeight: 1.45 }}>
          {t("Ce panier regroupe des abonnements sur")}{" "}
          <b>{t("plusieurs parkings")}</b>
          {t(", associés à la")} <b>{t("même filiale EFFIA")}</b>.{" "}
          {t("Les abonnements ayant la")} <b>{t("même date de début")}</b>{" "}
          {t("sont payés ensemble.")}
        </Typography>
      </Box>
    ) : null;

  const renderItem = (item: any, index: number, total: number, key: string) => {
    const price = getPrice(item);
    const name = getSubscriptionName(item);
    const parkingName = getParkingName(item);
    const address = getParkingAddress(item);
    const vehicle = getVehicle(item);
    const driver = getDriver(item);

    return (
      <React.Fragment key={item.id || key}>
        <Box
          sx={{
            px: 1.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            bgcolor: "#FFFFFF",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {/* Nom de l'abonnement */}
            <Typography
              sx={{ fontSize: 14, fontWeight: 700, color: COLORS.title }}
            >
              {name || t("Abonnement")}
            </Typography>

            {/* Nom du parking */}
            {parkingName && (
              <Typography
                sx={{ fontSize: 11, color: COLORS.muted, lineHeight: 1.5 }}
              >
                {parkingName}
              </Typography>
            )}

            {/* Adresse du parking */}
            {address && (
              <Typography
                sx={{ fontSize: 11, color: COLORS.muted, lineHeight: 1.5 }}
              >
                {address}
              </Typography>
            )}

            {/* Véhicule */}
            {vehicle && (
              <Typography
                sx={{ fontSize: 10, color: COLORS.light, lineHeight: 1.5 }}
              >
                {`${t("Véhicule")} : ${vehicle}`}
              </Typography>
            )}

            {/* Conducteur (à la ligne) */}
            {driver && (
              <Typography
                sx={{ fontSize: 10, color: COLORS.light, lineHeight: 1.5 }}
              >
                {`${t("Conducteur")} : ${driver}`}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {price !== null && (
              <Typography
                sx={{
                  flexShrink: 0,
                  fontSize: 14,
                  fontWeight: 700,
                  color: COLORS.title,
                }}
              >
                {formatPrice(price)}
              </Typography>
            )}

            {/* Bouton supprimer (croix) */}
            <IconButton
              size="small"
              aria-label={t("Supprimer")}
              onClick={() => handleRemoveItem(item)}
              sx={{
                width: 26,
                height: 26,
                border: `1px solid ${COLORS.border}`,
                borderRadius: "6px",
                color: "#8A97A5",
                "&:hover": { bgcolor: COLORS.header },
              }}
            >
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Box>
        {index < total - 1 && <Divider />}
      </React.Fragment>
    );
  };

  const renderGroup = (group: any) => {
    const total = group.items.reduce(
      (sum: number, item: any) => sum + (getPrice(item) || 0),
      0,
    );

    return (
      <Box
        key={group.key}
        sx={{
          mb: 2,
          border: `1px solid ${COLORS.border}`,
          borderLeft: `3px solid ${COLORS.orange}`,
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {/* En-tête : date de début + type de paiement */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            bgcolor: COLORS.header,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <CalendarTodayOutlined
              sx={{ fontSize: 13, color: COLORS.orange }}
            />
            <Typography
              sx={{ fontSize: 12, fontWeight: 600, color: COLORS.title }}
            >
              {group.date
                ? `${t("Début le")} ${group.date.toLocaleDateString(language)}`
                : t("Date de début à confirmer")}
            </Typography>
            <Chip
              size="small"
              label={
                group.items.length > 1
                  ? t("Paiement groupé")
                  : t("Paiement individuel")
              }
              sx={{
                height: 20,
                fontSize: 10,
                bgcolor: COLORS.chipBg,
                color: COLORS.orangeText,
              }}
            />
          </Box>
          {group.items.length > 1 && (
            <Typography
              sx={{ fontSize: 11, color: COLORS.muted, whiteSpace: "nowrap" }}
            >
              {group.items.length} {t("abonnements")}
            </Typography>
          )}
        </Box>

        <Divider />

        {/* Abonnements */}
        {group.items.map((item: any, index: number) =>
          renderItem(item, index, group.items.length, `${group.key}-${index}`),
        )}

        <Divider />

        {/* Total + actions */}
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            bgcolor: "#FFFFFF",
          }}
        >
          <Typography
            sx={{
              mr: "auto",
              display: "flex",
              alignItems: "baseline",
              gap: 1,
              fontSize: 12,
              color: COLORS.muted,
            }}
          >
            {t("Total à payer")}
            <Box
              component="span"
              sx={{ fontSize: 15, fontWeight: 700, color: COLORS.title }}
            >
              {formatPrice(total)}
            </Box>
          </Typography>
          <Button
            size="small"
            onClick={goToSubscriptions}
            sx={{ color: COLORS.muted, fontWeight: 600 }}
          >
            {t("Modifier")}
          </Button>
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={() => goToPaiement(group)}
            sx={{
              color: "white",
              fontWeight: 600,
              px: 2,
              borderRadius: "8px",
              bgcolor: COLORS.orange,
              "&:hover": { bgcolor: COLORS.orangeDark },
            }}
          >
            {t("Payer maintenant")}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderEntity = (entity: any) => (
    <Box key={entity.key} sx={{ mb: 1 }}>
      {/* Icône société + nom de l'entité juridique */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <BusinessOutlined sx={{ fontSize: 18, color: COLORS.title }} />
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.3px",
            textTransform: "uppercase",
            color: COLORS.title,
          }}
        >
          {entity.name || t("Entité")}
        </Typography>
      </Box>
      {entity.groups.map((group: any) => renderGroup(group))}
    </Box>
  );

  const renderContent = () => (
    <>
      {renderBanner()}
      {entities.map((entity: any) => renderEntity(entity))}
    </>
  );

  // -------------------------------------------------------------------------
  // Page complète
  // -------------------------------------------------------------------------

  if (fullPage) {
    return (
      <Box sx={{ minHeight: "65vh", bgcolor: "#F4F6F8", py: { xs: 2, md: 4 } }}>
        <Box sx={{ width: "min(1100px, 100% - 32px)", mx: "auto" }}>
          <Breadcrumbs sx={{ mb: 1.5, fontSize: 12 }}>
            <Link
              component={RouterLink}
              to="/"
              underline="hover"
              color="inherit"
            >
              {t("Accueil")}
            </Link>
            <Typography sx={{ fontSize: 12, color: COLORS.muted }}>
              {t("Mon panier")}
            </Typography>
          </Breadcrumbs>
          <Typography
            component="h1"
            sx={{ fontSize: 24, fontWeight: 700, color: COLORS.title }}
          >
            {t("Mon panier")}
          </Typography>
          <Typography sx={{ mb: 2, fontSize: 12, color: COLORS.muted }}>
            {itemCount} {t("abonnements en attente de paiement")}
          </Typography>

          {loading ? (
            <LinearProgress />
          ) : cartItems.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography sx={{ color: COLORS.muted }}>
                {t("Votre panier est vide")}
              </Typography>
            </Paper>
          ) : (
            <Paper sx={{ p: { xs: 1.5, md: 3 }, borderRadius: 1 }}>
              {renderContent()}
            </Paper>
          )}
        </Box>
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // Popover (icône du panier dans la navbar)
  // -------------------------------------------------------------------------

  return (
    <>
      <IconButton
        aria-label={t("Ouvrir le panier")}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchorEl)}
        onClick={openCart}
        color="inherit"
        sx={{ color: "white", mx: 0.5 }}
      >
        <Badge
          badgeContent={itemCount}
          color="primary"
          sx={{ "& .MuiBadge-badge": { backgroundColor: COLORS.orange } }}
        >
          <ShoppingCartOutlined />
        </Badge>
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: "min(480px, calc(100vw - 16px))",
              maxHeight: "min(540px, calc(100vh - 72px))",
              overflowY: "auto",
              borderRadius: "10px",
              boxShadow: "0 8px 28px rgba(0,0,0,0.24)",
            },
          },
        }}
      >
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          <Typography
            sx={{ fontWeight: 700, fontSize: 18, color: COLORS.title }}
          >
            {t("Mon panier")}
          </Typography>
          <Typography sx={{ fontSize: 12, color: COLORS.muted }}>
            {itemCount} {t("abonnements en attente de paiement")}
          </Typography>
        </Box>
        <Divider />

        {loading ? (
          <LinearProgress />
        ) : cartItems.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography sx={{ color: COLORS.muted, fontSize: 14 }}>
              {t("Votre panier est vide")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>{renderContent()}</Box>
        )}

        <Divider />
        <Button
          fullWidth
          onClick={goToCartPage}
          sx={{ py: 1.25, color: COLORS.title, fontWeight: 700 }}
        >
          {t("Voir tous")}
        </Button>
      </Popover>
    </>
  );
};

export default Cart;
